import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  Package, Upload, Send, MapPin, Phone, User, 
  FileText, IndianRupee, X, Image, CheckCircle,
  Truck, Shield, Clock, ArrowLeft, Star, Sparkles,
  ArrowRight, Camera
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: 'electronics', label: 'Electronics & Gadgets', icon: '📱' },
  { value: 'mobiles', label: 'Mobiles & Tablets', icon: '📲' },
  { value: 'fashion', label: 'Fashion & Clothing', icon: '👕' },
  { value: 'perfumes', label: 'Perfumes & Fragrances', icon: '🧴' },
  { value: 'watches', label: 'Watches & Accessories', icon: '⌚' },
  { value: 'beauty', label: 'Beauty & Cosmetics', icon: '💄' },
  { value: 'chocolates', label: 'Chocolates & Sweets', icon: '🍫' },
  { value: 'food', label: 'Food Items & Gourmet', icon: '🍽️' },
  { value: 'other', label: 'Other Items', icon: '📦' }
];

const popularRequests = [
  { name: 'iPhone 15 Pro Max', image: 'https://images.pexels.com/photos/788946/pexels-photo-788946.jpeg?auto=compress&cs=tinysrgb&w=200' },
  { name: 'Dubai Dates & Sweets', image: 'https://images.pexels.com/photos/5945755/pexels-photo-5945755.jpeg?auto=compress&cs=tinysrgb&w=200' },
  { name: 'Arabic Perfumes', image: 'https://images.pexels.com/photos/965989/pexels-photo-965989.jpeg?auto=compress&cs=tinysrgb&w=200' },
  { name: 'Luxury Watches', image: 'https://images.pexels.com/photos/190819/pexels-photo-190819.jpeg?auto=compress&cs=tinysrgb&w=200' },
];

const RequestItemPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestId, setRequestId] = useState('');
  const [formData, setFormData] = useState({
    customer_name: '',
    whatsapp_number: '',
    product_name: '',
    description: '',
    images: [],
    estimated_price: '',
    category: '',
    delivery_address: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.images.length + files.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }

    setUploading(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(`${API}/upload-image`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedUrls.push(`${BACKEND_URL}${response.data.url}`);
      }
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
      toast.success(`${uploadedUrls.length} image(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.whatsapp_number || !formData.product_name || !formData.delivery_address) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/item-requests`, {
        ...formData,
        estimated_price: formData.estimated_price ? parseFloat(formData.estimated_price) : null
      });
      
      setRequestId(response.data.id);
      setSubmitted(true);
      toast.success('Request submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 py-12 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto"
        >
          {/* Success Card */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            {/* Header Image */}
            <div className="relative h-48 bg-gradient-to-r from-[#0047AB] to-[#0066FF]">
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', delay: 0.2 }}
                  className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-xl"
                >
                  <CheckCircle className="w-12 h-12 text-emerald-500" />
                </motion.div>
              </div>
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-5 left-10 w-20 h-20 bg-white rounded-full" />
                <div className="absolute bottom-10 right-5 w-16 h-16 bg-white rounded-full" />
              </div>
            </div>

            <div className="p-8 text-center">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Request Submitted!</h1>
              <p className="text-slate-600 mb-4">
                Your request ID is
              </p>
              <div className="bg-slate-100 rounded-xl p-4 mb-6">
                <span className="font-mono text-2xl font-bold text-[#0047AB]">#{requestId}</span>
              </div>
              
              <div className="bg-emerald-50 rounded-xl p-4 mb-6 border border-emerald-100">
                <div className="flex items-center justify-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-emerald-800">Quotation within 24 hours</p>
                    <p className="text-sm text-emerald-600">We'll send it to your WhatsApp</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-100">
                <div className="flex items-center justify-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Truck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-blue-800">Delivery in 3 Days</p>
                    <p className="text-sm text-blue-600">After order confirmation</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="flex-1"
                >
                  Back to Home
                </Button>
                <Button
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({
                      customer_name: '',
                      whatsapp_number: '',
                      product_name: '',
                      description: '',
                      images: [],
                      estimated_price: '',
                      category: '',
                      delivery_address: ''
                    });
                  }}
                  className="flex-1 bg-[#0047AB] hover:bg-[#003380]"
                >
                  New Request
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Image - Same as Personal Shopping banner */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1761474257877-47bac32b03b6?auto=format&fit=crop&w=1920&q=80"
            alt="Shopping"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0047AB]/95 via-[#0047AB]/90 to-[#0066FF]/80" />
        </div>

        <div className="relative py-12 md:py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-white/80 hover:text-white hover:bg-white/10 mb-4 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white text-sm font-medium px-4 py-1.5 rounded-full mb-4">
                  <Sparkles className="w-4 h-4" />
                  On-Demand Delivery Service
                </span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                  Request Any Item<br />
                  <span className="text-blue-200">from Dubai</span>
                </h1>
                <p className="text-lg text-white/90 mb-6">
                  Can't find what you need? Tell us what you want and we'll source it from Dubai and deliver it to your doorstep anywhere in India!
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                    <Truck className="w-5 h-5 text-blue-200" />
                    <span className="text-sm font-medium text-white">3-Day Delivery</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                    <Shield className="w-5 h-5 text-blue-200" />
                    <span className="text-sm font-medium text-white">100% Authentic</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                    <Clock className="w-5 h-5 text-blue-200" />
                    <span className="text-sm font-medium text-white">24hr Quotation</span>
                  </div>
                </div>
              </motion.div>

              {/* Popular Requests */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="hidden md:block"
              >
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20">
                  <p className="text-white/80 text-sm font-medium mb-4">Popular Requests</p>
                  <div className="grid grid-cols-2 gap-3">
                    {popularRequests.map((item, index) => (
                      <motion.div
                        key={`pop-${item.name}`}
                        whileHover={{ scale: 1.02 }}
                        className="bg-white rounded-xl p-3 cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => setFormData(prev => ({ ...prev, product_name: item.name }))}
                      >
                        <img src={item.image} alt={item.name} className="w-full h-20 object-cover rounded-lg mb-2" />
                        <p className="text-xs font-medium text-slate-800 truncate">{item.name}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="max-w-3xl mx-auto px-4 -mt-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          {/* Form Header */}
          <div className="bg-gradient-to-r from-slate-50 to-blue-50 p-6 border-b border-slate-100">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Send className="w-5 h-5 text-[#0047AB]" />
              Submit Your Request
            </h2>
            <p className="text-sm text-slate-600 mt-1">Fill in the details and we'll get back to you within 24 hours</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
            {/* Personal Details */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-slate-900 font-semibold">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <User className="w-4 h-4 text-[#0047AB]" />
                </div>
                Your Details
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer_name">Full Name *</Label>
                  <Input
                    id="customer_name"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    required
                    className="mt-1.5"
                    data-testid="customer-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="whatsapp_number">WhatsApp Number *</Label>
                  <Input
                    id="whatsapp_number"
                    name="whatsapp_number"
                    value={formData.whatsapp_number}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    required
                    className="mt-1.5"
                    data-testid="whatsapp-input"
                  />
                </div>
              </div>
            </div>

            {/* Product Details */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 text-slate-900 font-semibold">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Package className="w-4 h-4 text-purple-600" />
                </div>
                Product Details
              </div>

              <div>
                <Label htmlFor="product_name">What do you want? *</Label>
                <Input
                  id="product_name"
                  name="product_name"
                  value={formData.product_name}
                  onChange={handleChange}
                  placeholder="e.g., iPhone 15 Pro Max 256GB Blue"
                  required
                  className="mt-1.5"
                  data-testid="product-name-input"
                />
              </div>

              <div>
                <Label htmlFor="description">Description & Specifications</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Provide any specific details - color, model number, brand preference, link to product..."
                  rows={3}
                  className="mt-1.5"
                  data-testid="description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1.5"
                    data-testid="category-select"
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.icon} {cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="estimated_price">Your Budget (₹)</Label>
                  <Input
                    id="estimated_price"
                    name="estimated_price"
                    type="number"
                    value={formData.estimated_price}
                    onChange={handleChange}
                    placeholder="Expected price range"
                    className="mt-1.5"
                    data-testid="price-input"
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <Label className="flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Reference Images (Optional)
                </Label>
                <p className="text-xs text-slate-500 mb-3 mt-1">Upload screenshots or reference images to help us find the exact product (max 5)</p>
                
                <div className="flex flex-wrap gap-3">
                  {formData.images.map((url, index) => (
                    <div key={`img-${index}-${url.slice(-12)}`} className="relative w-24 h-24 rounded-xl overflow-hidden border-2 border-slate-200 group">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >
                        <X className="w-6 h-6 text-white" />
                      </button>
                    </div>
                  ))}
                  
                  {formData.images.length < 5 && (
                    <label className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#0047AB] hover:bg-blue-50 transition-all">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                      {uploading ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0047AB]" />
                      ) : (
                        <>
                          <Image className="w-6 h-6 text-slate-400 mb-1" />
                          <span className="text-xs text-slate-400">Add Image</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Delivery Address */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 text-slate-900 font-semibold">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                </div>
                Delivery Address
              </div>

              <div>
                <Label htmlFor="delivery_address">Full Address *</Label>
                <Textarea
                  id="delivery_address"
                  name="delivery_address"
                  value={formData.delivery_address}
                  onChange={handleChange}
                  placeholder="Enter your complete delivery address with pincode"
                  rows={3}
                  required
                  className="mt-1.5"
                  data-testid="address-input"
                />
              </div>
            </div>

            {/* Submit */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-[#0047AB] hover:bg-[#003380] text-lg font-semibold shadow-lg shadow-blue-900/20"
                data-testid="submit-request-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    Submitting...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Submit Request
                    <ArrowRight className="w-5 h-5" />
                  </div>
                )}
              </Button>

              <p className="text-xs text-center text-slate-500 mt-4">
                We'll review your request and send a quotation to your WhatsApp within 24 hours.
              </p>
            </div>
          </form>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 grid grid-cols-3 gap-4"
        >
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-slate-100">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Shield className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-slate-800">100% Authentic</p>
            <p className="text-xs text-slate-500">Genuine products</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-slate-100">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Truck className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-sm font-medium text-slate-800">3-Day Delivery</p>
            <p className="text-xs text-slate-500">Fast shipping</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-slate-100">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Star className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-sm font-medium text-slate-800">500+ Orders</p>
            <p className="text-xs text-slate-500">Happy customers</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RequestItemPage;
